import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { voiceApi } from '@/api/services/voice.api';
import { handleMutationError } from '@/lib/handle-mutation-error';
import { useCurrentUser } from '@/features/auth/hooks/use-current-user';
import { usePermissions } from '@/hooks/use-permissions';
import { PERMISSION } from '@/features/users-roles/constants';
import { voiceKeys } from '../query-keys';
import type { IVoicePresenceEntry, VoicePresenceStatus } from '../types';

/**
 * Org-wide availability snapshot (users without a stored row are absent =
 * ACTIVE). The endpoint requires VOICE_CALLS_READ, so the fetch is gated on
 * it here — a receive-only agent never fires a query it would 403 on.
 */
export function useVoicePresence() {
    const { hasAnyPermission, isLoading: permissionsLoading } = usePermissions();
    const canReadPresence = hasAnyPermission([PERMISSION.VOICE_CALLS_READ]);

    return useQuery({
        queryKey: voiceKeys.presence(),
        queryFn: voiceApi.getPresence,
        staleTime: 15_000,
        enabled: !permissionsLoading && canReadPresence,
    });
}

/** The signed-in agent's own manual status — defaults to ACTIVE when unset. */
export function useMyVoicePresenceStatus(): VoicePresenceStatus {
    const { data: me } = useCurrentUser();
    const { data: presence } = useVoicePresence();
    return presence?.find((entry) => entry.userId === me?.id)?.status ?? 'ACTIVE';
}

export function useSetMyVoicePresence() {
    const queryClient = useQueryClient();
    const { data: me } = useCurrentUser();

    return useMutation({
        mutationFn: voiceApi.setMyPresence,
        onSuccess: (_data, status) => {
            // Write-through so agents without VOICE_CALLS_READ (whose org
            // snapshot fetch is disabled) still see their new status.
            const myUserId = me?.id;
            if (myUserId) {
                queryClient.setQueryData<IVoicePresenceEntry[]>(voiceKeys.presence(), (prev) => {
                    const mine = prev?.find((entry) => entry.userId === myUserId);
                    const rest = prev?.filter((entry) => entry.userId !== myUserId) ?? [];
                    return [
                        ...rest,
                        { userId: myUserId, status, connected: mine?.connected ?? false },
                    ];
                });
            }
            void queryClient.invalidateQueries({ queryKey: voiceKeys.presence() });
        },
        onError: (error: unknown) => handleMutationError(error, 'Failed to update your status'),
    });
}
